import React from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { Col, Row, Typography } from 'antd';
import { Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

import FormItem from 'components/formik/FormItem/FormItem';
import SubmitButton from 'components/formik/SubmitButton/SubmitButton';
import { PasswordValidationRules } from 'utils/authUtils';
import PasswordValidationRuleBox from 'features/auth/PasswordValidationRuleBox';
import logo from 'assets/img/logo-dark.svg';

import style from 'features/auth/AuthorizationPage.module.less';

const { Title } = Typography;

const requiredMessage = 'Field is required';

const initialValidationSchema = Yup.object({
  businessName: Yup.string()
    .trim()
    .required(requiredMessage)
    .max(250, 'Business name should be 250 characters maximum'),
  firstName: Yup.string()
    .required(requiredMessage)
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .required(requiredMessage)
    .max(120, 'Last name should be 120 characters maximum'),
  email: Yup.string()
    .required(requiredMessage)
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  password: Yup.string()
    .required(requiredMessage)
    .matches(PasswordValidationRules.MinimumCharacters)
    .matches(PasswordValidationRules.MaximumCharacters)
    .matches(PasswordValidationRules.LowercaseCharacter)
    .matches(PasswordValidationRules.SpecialCharacter)
    .matches(PasswordValidationRules.UppercaseCharacter)
    .matches(PasswordValidationRules.OneNumber),
});

export interface RegistrationFormValues {
  businessName: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

type Props = {
  initialValues: RegistrationFormValues;
  handleSubmit: (values: RegistrationFormValues) => void;
  isEmailDisabled?: boolean;
  isBusinessNameRequired?: boolean;
};

export default function FormRegistration(props: Props) {
  const {
    initialValues,
    handleSubmit,
    isEmailDisabled = false,
    isBusinessNameRequired = true,
  } = props;
  const validationSchema = isBusinessNameRequired
    ? initialValidationSchema
    : initialValidationSchema.omit(['businessName']);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {(formik) => (
        <Form className={style.authPageForm} layout="vertical">
          <div className={style.authPageTitle}>
            <div>Welcome to</div>
            <div className={style.authPageLogo}>
              <img src={logo} alt="myBasePay" />
            </div>
          </div>
          <Title level={2} className={style.authPageSubtitle}>
            Let’s get you through quick setup.
          </Title>
          <div className={style.authPageContent}>
            {isBusinessNameRequired && (
              <FormItem name="businessName" label="Business name *">
                <Input size="middle" name="businessName" autoComplete="off" />
              </FormItem>
            )}

            <FormItem name="firstName" label="First name *">
              <Input size="middle" name="firstName" autoComplete="off" />
            </FormItem>

            <FormItem name="lastName" label="Last name *">
              <Input size="middle" name="lastName" autoComplete="off" />
            </FormItem>

            <FormItem name="email" label="Email address *">
              <Input
                size="middle"
                name="email"
                disabled={isEmailDisabled}
                autoComplete="off"
              />
            </FormItem>

            <FormItem
              name="password"
              label="Password *"
              showValidateError={formik.errors.password === requiredMessage}
            >
              <Input.Password
                size="middle"
                name="password"
                iconRender={(visible: boolean) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
                autoComplete="off"
              />
            </FormItem>

            <PasswordValidationRuleBox password={formik.values.password} />

            <Row gutter={20} align="middle" className={style.submitBox}>
              <Col xs={{ span: 24, order: 2 }} md={{ span: 10, order: 0 }}>
                <SubmitButton type="primary" size="large" block>
                  Continue
                </SubmitButton>
              </Col>
              <Col xs={24} md={14}>
                <div className={style.authPageText}>
                  By clicking Continue, you confirm that you understand and
                  agree to myBasePay{' '}
                  <Link to="/terms-and-conditions" target="_blank">
                    Terms and Conditions.
                  </Link>
                </div>
              </Col>
            </Row>
          </div>
        </Form>
      )}
    </Formik>
  );
}
